import {
  Component,
  computed,
  effect,
  ElementRef,
  inject,
  OnInit,
  signal,
  Signal,
  TemplateRef,
  viewChild
} from '@angular/core';
import {ActivatedRoute, Router, RouterLink} from "@angular/router";
import {FormControl, FormGroup, FormsModule, ReactiveFormsModule} from "@angular/forms";
import {CardWrapComponent} from "../../../shared/card-wrap/card-wrap/card-wrap.component";
import {InputComponent} from "../../../shared/form-elements/input/input/input.component";
import {SelectComponent, SelectOption} from "../../../shared/form-elements/select/select/select.component";
import {AsyncPipe, NgTemplateOutlet} from "@angular/common";
import {getProductForm, ProductForm} from "./forms/product.form";
import {getNewModel3dForm} from "./forms/model3d.form";
import {environment} from 'src/environments/environment.dev';
import {toSignal} from "@angular/core/rxjs-interop";
import {getNewLamellaForm} from "./forms/lamella.form";
import {productHandlingStore} from './store/product-handling.store';
import {convertNestedFormGroupToFormData} from "../../../shared/util/formGroupToFormData";
import {isDefined} from "../../../core/util/isDefined";
import {Observable} from "rxjs";
import {injectFileHtmlInput} from "../../../shared/util/injectFile-html-input";
import {TranslatePipe} from "@ngx-translate/core";

@Component({
  selector: 'app-product-handling',
  imports: [
    RouterLink,
    FormsModule,
    CardWrapComponent,
    InputComponent,
    SelectComponent,
    NgTemplateOutlet,
    ReactiveFormsModule,
    AsyncPipe,
    TranslatePipe
  ],
  templateUrl: './product-handling.component.html',
  styleUrl: './product-handling.component.scss'
})
export class ProductHandlingComponent implements OnInit {
  private productHandlingStore = inject(productHandlingStore);
  private router = inject(Router)
  private activatedRoute = inject(ActivatedRoute);


  protected productForm: FormGroup<ProductForm> = getProductForm(this.productHandlingStore.current());

  protected productType = toSignal(this.productForm.controls.productType.valueChanges);

  protected isNewProductType = computed(()=>this.productType() === "NEW_PRODUCT_TYPE");

  protected installationDirections:Array<SelectOption> = environment.installationDirections.map(i => ( { value: i, label: i } ) );

  protected productTypes:Array<SelectOption> = environment.productTypes.map(i => ( { value: i, label: i } ) );

  protected lamellaFamilies:Array<SelectOption> = environment.lamellaFamilies.map((i, idx) => ( { value: idx+1, label: i } ) );

  protected submitAction = signal(false);
  protected coverImage: Signal<ElementRef<HTMLInputElement>> = viewChild('coverImage');
  protected measureImage: Signal<ElementRef<HTMLInputElement>> = viewChild('measureImage');
  protected lamellaTemplate: Signal<TemplateRef<any>> = viewChild('lamellaTemplate');
  protected newProductTypeTemplate: Signal<TemplateRef<any>> = viewChild('newProductTypeTemplate');

  private readonly templates: {[key:string|symbol]: Signal<TemplateRef<any>>} = {
    default: this.newProductTypeTemplate,
    new_product_type: this.newProductTypeTemplate,
    lamella: this.lamellaTemplate
  }

  protected productTypeConfigTemplate = computed(() =>
      this.templates[this.productType()] !== undefined
          ? this.templates[this.productType()]()
          : this.templates['default']()
  )

  protected lamelSpaces: Array<SelectOption> = (()=> {
    let val = 26.5;
    const arr: Array<number> = [];
    while (val <= environment.lamellaMaxSpace) { arr.push(val); val += environment.lamellaSpan }
    return arr;
  })().map(i => ({value: i, label: `${i} mm`}));

  ngOnInit() {
    this.handleLamellaForm();
    this.setFileOnInput(this.productForm.controls.measureImageFile.valueChanges, this.measureImage);
    this.setFileOnInput(this.productForm.controls.coverImageFile.valueChanges, this.coverImage);
  }

  private setFileOnInput(fileControl: Observable<File>, inputElement: Signal<ElementRef<HTMLInputElement>>){
    fileControl.subscribe((file) => injectFileHtmlInput(inputElement().nativeElement, file))
  }

  private handleLamellaForm(){
    this.productForm.controls.productType.valueChanges.subscribe((value) => {
      value === 'lamella'
          ? this.productForm.setControl('lamella', getNewLamellaForm(this.productHandlingStore.current()?.lamella))
          : this.productForm.setControl('lamella', undefined);
    });
  }

  handleFileLoad< key extends keyof ProductForm >(fileList: FileList, controlName: key) {
    if(fileList.length < 1 ) { return; }
    if(fileList.length > 1) { console.warn(`Only one file can be uploaded at a time. Uploading ${fileList[0].name}` ); }
    (this.productForm.controls[controlName] as FormControl<File>).setValue(fileList[0]);
  }

  handleModel3dLoad(fileList: FileList) {
    if(fileList.length < 1 ) { return; }
    if(fileList.length > 1) { console.warn(`Only one file can be uploaded at a time. Uploading ${fileList[0].name}` ); }

    const file: File = new File([fileList[0]], fileList[0].name, {type: 'octet-stream'});

    this.productForm.controls.model3d.push(getNewModel3dForm(file));
  }

  handleModel3dRemove(index: number) {
    this.productForm.controls.model3d.removeAt(index);
  }

  onSubmit(){
    this.submitAction.set(true)

    if (!this.productForm.valid){
        this.submitAction.set(false);
        return;
    }

    this.productHandlingStore.clearCurrent();
    const formData = convertNestedFormGroupToFormData(this.productForm);

    if (!isDefined(this.productForm.controls.id.value)) {
      this.productHandlingStore.createNewProduct(formData)
          .subscribe(() => {
            this.router.navigate(['../'], { relativeTo: this.activatedRoute });
          });
    } else {
      this.productHandlingStore.updateProduct(formData)
          .subscribe(() => {
            this.router.navigate(['../'], { relativeTo: this.activatedRoute });
          });
    }
  }

  clearCache(){
    this.productHandlingStore.clearCurrent();
  }
}
